/**
 * Millwork — baseboard, door and window casing, crown — is called that in
 * code because "trim" already names the cabinet-trim share
 * (`cabinetTrimRate`, books `LINE_KIND.trim`). The screen says "Trim".
 * Design: claude-docs/2026-10-16-trim-and-paint-design.md.
 *
 * Pure.
 */

/**
 * The trim a wall gets when nothing has set one: the wall panel's own
 * defaults (`WallInfoModal`), with baseboard on (spec §1, decision 3).
 * Baseboard is pure white like door and window casing (`TRIM_HEX.white`), so
 * a room's default trim is one paint row and the panel shows it as White.
 * The panel's old off-white `#f5f5f5` matched no swatch and split the paint.
 */
const DEFAULT_WALL_TRIM = Object.freeze({
  hasBaseboard: true,
  hasCrownMolding: false,
  style: 'traditional',
  crownColor: '#ffffff',
  baseboardColor: '#ffffff',
});

/** The three profiles, sized in metres as the 3D view draws them. */
const TRIM_STYLE_PRESETS = Object.freeze({
  traditional: Object.freeze({
    label: 'Traditional',
    description: '6" crown, 5" base',
    crownHeight: 0.15,
    baseHeight: 0.12,
  }),
  modern: Object.freeze({
    label: 'Modern',
    description: '3" crown, 3" base',
    crownHeight: 0.08,
    baseHeight: 0.08,
  }),
  minimal: Object.freeze({
    label: 'Minimal',
    description: '2" crown, 2" base',
    crownHeight: 0.05,
    baseHeight: 0.05,
  }),
});

/** Door and window casing face width when a side sets none (3.9″). */
const CASING_WIDTH_M = 0.1;

/**
 * A trim object's profile. A style the presets do not know reads as the
 * default's, which is what the 3D view's `getStyleDimensions` draws for it.
 */
const trimStyleOf = (trim) =>
  Object.hasOwn(TRIM_STYLE_PRESETS, trim?.style)
    ? trim.style
    : DEFAULT_WALL_TRIM.style;

/** The wall with the default trim when it has none; otherwise the wall itself. */
const withDefaultTrim = (wall) =>
  wall && wall.trim === undefined
    ? { ...wall, trim: { ...DEFAULT_WALL_TRIM } }
    : wall;

/**
 * Every wall of the room that has no trim gets the default. An explicit
 * `hasBaseboard: false` stays off. Returns the room by identity when no wall
 * changed.
 */
const ensureBaseboard = (room) => {
  if (!room || !Array.isArray(room.walls)) return room;
  let changed = false;
  const walls = room.walls.map((wall) => {
    const next = withDefaultTrim(wall);
    if (next !== wall) changed = true;
    return next;
  });
  return changed ? { ...room, walls } : room;
};

module.exports = {
  DEFAULT_WALL_TRIM,
  TRIM_STYLE_PRESETS,
  CASING_WIDTH_M,
  trimStyleOf,
  withDefaultTrim,
  ensureBaseboard,
};
